"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Video, Filter, Users } from "lucide-react";

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`;
const getToken = () => localStorage.getItem("access_token");

const formatDateTime = (dateTimeStr) => {
    const date = new Date(dateTimeStr);
    return date.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const isMeetingActive = (dateTimeStr, duree) => {
    const now = new Date();
    const start = new Date(dateTimeStr);
    const end = new Date(start.getTime() + duree * 60000);
    return now >= start && now <= end;
};

const isMeetingPassed = (dateTimeStr, duree) => {
    const now = new Date();
    const end = new Date(new Date(dateTimeStr).getTime() + duree * 60000);
    return now > end;
};

const EventCard = ({ event, onOpenDetails, onJoin, userRole }) => {
    const active = isMeetingActive(event.date_heure, event.duree);
    const passed = isMeetingPassed(event.date_heure, event.duree);
    const [hover, setHover] = useState(false);
    
    // Admin cannot join meetings - only view details
    const canJoin = active && userRole !== 'Admin';

    const getStatusStyle = () => {
        if (active) return { borderLeft: "4px solid #22c55e", background: "#f0fdf4" };
        if (passed) return { borderLeft: "4px solid #9ca3af", background: "#f9fafb", opacity: 0.8 };
        return { borderLeft: "4px solid #5e17eb" };
    };

    const getStatusText = () => {
        if (active) return "En cours";
        if (passed) return "Terminee";
        return "Planifiee";
    };

    const getStatusColor = () => {
        if (active) return "#22c55e";
        if (passed) return "#9ca3af";
        return "#5e17eb";
    };

    const getButtonText = () => {
        if (active) return "Participer";
        if (passed) return "Terminee";
        return "A venir";
    };

    return (
        <div
            onClick={onOpenDetails}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                ...styles.card,
                ...getStatusStyle(),
                transform: hover ? "translateY(-6px) scale(1.03)" : "translateY(0)",
                boxShadow: hover
                    ? "0 12px 30px rgba(94,23,235,0.25)"
                    : "0 4px 14px rgba(0,0,0,0.06)",
            }}
        >
            <div style={styles.cardHeader}>
                <Calendar size={40} color="#5e17eb" />
            </div>

            <div style={styles.cardBody}>
                <div style={styles.cardHeaderRow}>
                    <h3 style={styles.cardTitle}>{event.titre}</h3>
                    <span style={{
                        ...styles.statusBadge,
                        backgroundColor: getStatusColor() + '20',
                        color: getStatusColor()
                    }}>
                        {getStatusText()}
                    </span>
                </div>

                <p style={styles.cardOrganizer}>
                    Organise par: <strong>{event.organisateur_nom || event.organisateur}</strong>
                </p>

                <p style={styles.cardDate}>
                    {formatDateTime(event.date_heure)} ({event.duree} min)
                </p>
                <p style={styles.cardObjective}>{event.objectif}</p>
                <p style={styles.cardRoom}>📍 {event.salle}</p>

                <div style={styles.participants}>
                    <Users size={14} color="#666" />
                    <span>{event.participants_details?.length || 0} participants</span>
                </div>

                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        if (canJoin) onJoin();
                    }}
                    disabled={!canJoin}
                    style={{
                        ...styles.joinButton,
                        backgroundColor: canJoin ? "#16a34a" : "#e5e7eb",
                        color: canJoin ? "white" : "#9ca3af",
                        cursor: canJoin ? "pointer" : "not-allowed",
                    }}
                >
                    <Video size={18} />
                    {getButtonText()}
                </button>
            </div>
        </div>
    );
};

const AdminReunions = () => {
    const navigate = useNavigate();
    const [eventsData, setEventsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState("all");
    const [userRole, setUserRole] = useState(null);

    useEffect(() => {
        const authData = JSON.parse(localStorage.getItem("auth_data") || "{}");
        setUserRole(authData.role);
    }, []);

    const fetchReunions = async () => {
        setLoading(true);
        setError("");
        try {
            const token = getToken();
            if (!token) {
                setError("Vous devez etre connecte");
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_URL}/reunions/`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                throw new Error("Erreur lors du chargement des reunions");
            }

            const data = await response.json();

            let meetingsData = [];
            if (data.results) meetingsData = data.results;
            else if (Array.isArray(data)) meetingsData = data;
            else if (data.data) meetingsData = data.data;

            const uniqueMeetings = [];
            const seenIds = new Set();
            for (const meeting of meetingsData) {
                if (!seenIds.has(meeting.id)) {
                    seenIds.add(meeting.id);
                    uniqueMeetings.push(meeting);
                }
            }

            setEventsData(uniqueMeetings);
        } catch (err) {
            console.error("Erreur fetch:", err);
            setError(err.message || "Erreur de chargement");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReunions();
    }, []);

    const handleJoinMeeting = (meetingId) => {
        if (userRole === 'Admin') {
            navigate(`/reunions/${meetingId}`);
        } else {
            navigate(`/consulter-reunion/${meetingId}`);
        }
    };

    const getFilteredEvents = () => {
        const now = new Date();

        return eventsData
            .filter(event => {
                const eventDate = new Date(event.date_heure);
                const eventEnd = new Date(eventDate.getTime() + (event.duree || 0) * 60000);

                switch (filter) {
                    case "upcoming":
                        return eventDate > now;
                    case "passed":
                        return eventEnd < now;
                    default:
                        return true;
                }
            })
            .sort((a, b) => {
                const aActive = isMeetingActive(a.date_heure, a.duree);
                const bActive = isMeetingActive(b.date_heure, b.duree);
                const aPassed = isMeetingPassed(a.date_heure, a.duree);
                const bPassed = isMeetingPassed(b.date_heure, b.duree);

                if (aActive && !bActive) return -1;
                if (!aActive && bActive) return 1;
                if (!aPassed && bPassed) return -1;
                if (aPassed && !bPassed) return 1;
                return new Date(a.date_heure) - new Date(b.date_heure);
            });
    };

    const filteredEvents = getFilteredEvents();

    const totalEvents = eventsData.length;
    const activeEvents = eventsData.filter(e => isMeetingActive(e.date_heure, e.duree)).length;
    const upcomingEvents = eventsData.filter(e => new Date(e.date_heure) > new Date() && !isMeetingActive(e.date_heure, e.duree)).length;
    const passedEvents = eventsData.filter(e => {
        const end = new Date(new Date(e.date_heure).getTime() + (e.duree || 0) * 60000);
        return end < new Date();
    }).length;

    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.loadingContainer}>
                    <div style={styles.spinner}></div>
                    <p>Chargement des reunions...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.page}>
                <div style={styles.errorContainer}>
                    <p style={{ color: "#ef4444" }}>❌ {error}</p>
                    <button onClick={fetchReunions} style={styles.retryButton}>
                        Reessayer
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <h1 style={styles.title}>LISTE DES REUNIONS</h1>
            <p style={styles.subtitle}>
                Consultez toutes les reunions de la plateforme
            </p>

            <div style={styles.statsBar}>
                <div style={styles.statItem}>
                    <span style={styles.statValue}>{totalEvents}</span>
                    <span style={styles.statLabel}>Total</span>
                </div>
                <div style={styles.statItem}>
                    <span style={{ ...styles.statValue }}>{activeEvents}</span>
                    <span style={styles.statLabel}>En cours</span>
                </div>
                <div style={styles.statItem}>
                    <span style={{ ...styles.statValue }}>{upcomingEvents}</span>
                    <span style={styles.statLabel}>A venir</span>
                </div>
                <div style={styles.statItem}>
                    <span style={{ ...styles.statValue }}>{passedEvents}</span>
                    <span style={styles.statLabel}>Passees</span>
                </div>
            </div>

            <div style={styles.topBar}>
                <div style={styles.filterGroup}>
                    <Filter size={18} color="#666" />
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        style={styles.filterSelect}
                    >
                        <option value="all">Toutes les reunions</option>
                        <option value="upcoming">A venir</option>
                        <option value="passed">Passees</option>
                    </select>
                </div>
            </div>

            {filteredEvents.length === 0 ? (
                <div style={styles.emptyState}>
                    <Calendar size={48} color="#9ca3af" />
                    <p>Aucune reunion a afficher</p>
                </div>
            ) : (
                <div style={styles.grid}>
                    {filteredEvents.map((event) => (
                        <EventCard
                            key={event.id}
                            event={event}
                            userRole={userRole}
                            onOpenDetails={() => navigate(`/reunions/${event.id}`)}
                            onJoin={() => handleJoinMeeting(event.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminReunions;

const styles = {
    page: {
        padding: "22px",
        background: "#f7f6ff",
        minHeight: "100vh",
        borderRadius: "16px"
    },
    title: {
        fontSize: "26px",
        color: "#1f1f2e",
        marginBottom: "4px",
    },
    subtitle: {
        color: "#666",
        marginBottom: "18px",
    },
    loadingContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "400px",
        gap: "16px"
    },
    spinner: {
        width: "40px",
        height: "40px",
        border: "3px solid #f3f3f3",
        borderTop: "3px solid #5e17eb",
        borderRadius: "50%",
        animation: "spin 1s linear infinite"
    },
    errorContainer: {
        textAlign: "center",
        padding: "40px",
        background: "white",
        borderRadius: "12px"
    },
    retryButton: {
        marginTop: "12px",
        padding: "8px 16px",
        background: "#5e17eb",
        color: "white",
        border: "none",
        borderRadius: "8px",
        cursor: "pointer"
    },
    statsBar: {
        display: "flex",
        gap: "24px",
        background: "white",
        padding: "16px 24px",
        borderRadius: "12px",
        marginBottom: "18px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
    },
    statItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
    },
    statValue: {
        fontSize: "24px",
        fontWeight: "bold",
        color: "#1f1f2e"
    },
    statLabel: {
        fontSize: "12px",
        color: "#666",
        marginTop: "4px"
    },
    topBar: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        marginBottom: "18px"
    },
    filterGroup: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        background: "white",
        padding: "4px 12px",
        borderRadius: "8px",
        border: "1px solid #e5e7eb"
    },
    filterSelect: {
        padding: "8px",
        border: "none",
        background: "transparent",
        outline: "none",
        cursor: "pointer",
        fontSize: "14px"
    },
    emptyState: {
        textAlign: "center",
        padding: "60px",
        background: "white",
        borderRadius: "12px",
        color: "#6b7280"
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
        gap: "16px"
    },
    card: {
        background: "white",
        borderRadius: "14px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    cardHeader: {
        height: "120px",
        background: "#f3f0ff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },
    cardBody: {
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        flex: 1
    },
    cardHeaderRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "8px",
        flexWrap: "wrap",
        gap: "8px"
    },
    cardTitle: {
        fontWeight: "600",
        fontSize: "16px",
        color: "#1f1f2e",
        margin: 0
    },
    statusBadge: {
        fontSize: "11px",
        padding: "4px 10px",
        borderRadius: "20px",
        fontWeight: "500"
    },
    cardOrganizer: {
        fontSize: "12px",
        color: "#5e17eb",
        marginBottom: "6px",
    },
    cardDate: {
        fontSize: "13px",
        color: "#888",
        marginBottom: "8px"
    },
    cardObjective: {
        fontSize: "13px",
        color: "#4b5563",
        marginBottom: "8px",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden"
    },
    cardRoom: {
        fontSize: "12px",
        color: "#6b7280",
        marginBottom: "8px"
    },
    participants: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "12px",
        color: "#666",
        marginBottom: "12px"
    },
    joinButton: {
        marginTop: "8px",
        padding: "8px 0",
        borderRadius: "8px",
        fontWeight: "500",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "6px",
        border: "none",
        width: "100%"
    },
};

const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);